// /src/renderer/layout/MainShell/MainShell.tsx
import React, { Component, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import TopMenu from '../TopMenu';
// import './MainShell.scss';
import styles from './MainShell.module.scss';
import useWebsocket from '@hooks/WebSocket/useWebSocket';
// import { ReactComponent as AlertClose } from 'img/AlertClose.svg';
import { ReactComponent as Primary } from 'img/alertPrimary.svg';
import { ReactComponent as Secondary } from 'img/alertSecondary.svg';
import { ReactComponent as FailedIcon } from 'img/success_icon.svg';
import { ReactComponent as SuccessIcon } from 'img/failed_icon.svg';
import { ReactComponent as WarningIcon } from 'img/triangle-exclamation.svg';
import { useConfig } from 'config/useConfig';
import { useSettingsModal } from '@features/analysis/analysisModal/SettingsModal/useSettingsModal';
import { renderers } from '@features/analysis/rendering/Panels/renderState';
import useAuth from '@hooks/Auth/useAuth';
import { useAuthPage } from '@features/auth/useAuthPage';
import { getServerConfig, uploadconfig } from '@api/serverconfig';
import { configStore } from 'config/configStore';
import { PARTITION_KEYS } from 'config/config';
import { setCloseAlertsByEscape } from 'utils/hotKeyActions';
import { ensureServerFeatureStateLoaded } from 'config/serverFeatureState';
interface Alert {
  id: string;
  type: 'success' | 'error' | 'storage';
  message: string;
  important?: boolean;
  storagePercentKey?: string;
}

const COMMON_CONFIG_KEYS = new Set<string>(
  PARTITION_KEYS.server as readonly string[],
);
const isRecord = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === 'object' && !Array.isArray(value);


const normalizeStorageAlertMessage = (payload: any): string => {
  const rawMessage = payload?.message;
  if (rawMessage == null) return '';

  const message = String(rawMessage).trim();
  if (!message) return '';

  return message.includes('%') ? message : `${message}%`;
};

const extractStorageAlertPercentKey = (payload: any, message: string): string => {
  const candidates = [
    payload?.data?.chargedPercentage,
    payload?.data?.usagePercentage,
    payload?.data?.percent,
    payload?.chargedPercentage,
    payload?.usagePercentage,
    payload?.percent,
    payload?.message,
    message,
  ];

  for (const candidate of candidates) {
    if (candidate == null) continue;

    if (typeof candidate === 'number' && Number.isFinite(candidate)) {
      return candidate.toFixed(2);
    }

    const match = String(candidate).match(/(\d+(?:\.\d+)?)\s*%?/);
    if (!match) continue;

    const parsed = Number(match[1]);
    if (Number.isFinite(parsed)) {
      return parsed.toFixed(2);
    }
  }

  return message.trim();
};

const extractServerCommon = (
  payload: unknown,
): Record<string, unknown> | null => {
  if (!isRecord(payload)) return null;

  const candidates: unknown[] = [
    payload,
    payload.data,
    payload.config,
    payload.commonConfig,
    payload.versionInfo,
  ];

  for (const candidate of candidates) {
    if (!isRecord(candidate)) continue;
    if (Object.keys(candidate).some((k) => COMMON_CONFIG_KEYS.has(k))) {
      return candidate;
    }
  }

  return null;
};

function MainShell(): React.JSX.Element {
  const { config } = useConfig();
  const { accessToken } = useAuth();
  const { user } = useAuthPage();
  const { general, setGeneralSetting } = useSettingsModal();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [alertButton, setAlertButton] = useState<boolean>(false);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const alertsRef = useRef<Alert[]>([]);
  const lastStorageAlertPercentKeyRef = useRef<string | null>(null);
  const [showAllAlerts, setShowAllAlerts] = useState(false);
  const [isConfigSyncReady, setIsConfigSyncReady] = useState(false);
  const location = useLocation();
  const isAnalysisPage = location.pathname.includes('analysis');
  const socket = useWebsocket();
  const isAdmin = user?.role?.name === 'admin';

  const updateAlerts = (updater: (prev: Alert[]) => Alert[]) => {
    setAlerts((prev) => {
      const next = updater(prev);
      alertsRef.current = next;
      return next;
    });
  };

  const closeAlerts = () => {
    setAlertButton(false);
    alertsRef.current = [];
    setAlerts([]);
  };

  useEffect(() => {
    setCloseAlertsByEscape(alertButton ? closeAlerts : null);
    return () => setCloseAlertsByEscape(null);
  }, [alertButton]);

  // config ?�팅 로드
  useEffect(() => {
    if (config.generalSetting) setGeneralSetting(config.generalSetting);
  }, []);

  useEffect(() => {
    if (!accessToken) return;
    ensureServerFeatureStateLoaded(accessToken);
  }, [accessToken]);

  useEffect(() => {
    setIsConfigSyncReady(false);
    if (!accessToken) return;

    let cancelled = false;

    (async () => {
      try {
        const persistedServerCommon =
          await configStore.getPersistedServerCommonOrNull();
        const fallbackServerCommon =
          persistedServerCommon ?? configStore.getDefaultServerCommon();

        const [status, payload] = await getServerConfig(accessToken);
        if (cancelled) return;

        const serverCommon = status ? extractServerCommon(payload) : null;

        if (serverCommon && Object.keys(serverCommon).length > 0) {
          configStore.mergeServerCommonFromServer(serverCommon);
          if (isAdmin) {
            const nextServerPayload = configStore.composeServerCommonForUpload(
              serverCommon,
              fallbackServerCommon,
            );
            await uploadconfig(accessToken, nextServerPayload);
          }
        } else if (isAdmin) {
          // Server common config is empty: use local common.json seed if present, else defaults.
          await uploadconfig(accessToken, fallbackServerCommon);
        }

        if (!cancelled) setIsConfigSyncReady(true);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to sync common config from server', err);
        setIsConfigSyncReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [accessToken, isAdmin]);

  useEffect(() => {
    if (!accessToken || !isAdmin || !isConfigSyncReady) return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    let prev = JSON.stringify(configStore.getServerCommon());

    const unsubscribe = configStore.subscribe(() => {
      const next = JSON.stringify(configStore.getServerCommon());
      if (next === prev) return;
      prev = next;

      if (timer) clearTimeout(timer);
      timer = setTimeout(async () => {
        try {
          await uploadconfig(accessToken, configStore.getServerCommon());
        } catch (err) {
          console.error('Failed to upload common config', err);
        }
      }, 500);
    });

    return () => {
      if (timer) clearTimeout(timer);
      unsubscribe();
    };
  }, [accessToken, isAdmin, isConfigSyncReady]);

  useEffect(() => {
    const currentLabel = general.theme;
    document.documentElement.setAttribute('data-theme', currentLabel === 'Light Mode'?'light':'dark');
  }, [general.theme]);

  const generateId = () => {
    return `id-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
  };

  useEffect(() => {
    if (!socket) return;

    const handleSuccess = (payload: any, acknowledge?: () => void) => {
      setAlertButton(true);
      setShowAllAlerts(false);
      const frames = payload?.data?.frames;
      updateAlerts((prev) => [
        ...prev,
        {
          id: generateId(),
          type: 'success',
          message: `Success to import : Series(${frames})`,
          important: false,
        },
      ]);
      acknowledge?.();
    };

    const handleError = (payload: any, acknowledge?: () => void) => {
      setAlertButton(true);
      setShowAllAlerts(false);
      const frames = payload?.data?.frames;
      const message = payload?.data?.message;
      let mes = `Failed to import : Series(${frames})`;
      if (message) {
        for (const key in message) {
          if (message[key] !== '') {
            mes += '\n' + message[key];
          }
        }
      }
      updateAlerts((prev) => [
        ...prev,
        {
          id: generateId(),
          type: 'error',
          message: mes,
          important: false,
        },
      ]);
      acknowledge?.();
    };

    const handleStorageAlert = (payload: any) => {
      if (general.storageAlertNotification === false) return;

      const message = normalizeStorageAlertMessage(payload);
      const storagePercentKey = extractStorageAlertPercentKey(payload, message);
      if (!message || !storagePercentKey) return;

      if (lastStorageAlertPercentKeyRef.current === storagePercentKey) return;

      const hasSameStorageAlert = alertsRef.current.some(
        (alert) =>
          alert.type === 'storage' &&
          alert.storagePercentKey === storagePercentKey,
      );
      if (hasSameStorageAlert) {
        lastStorageAlertPercentKeyRef.current = storagePercentKey;
        return;
      }

      lastStorageAlertPercentKeyRef.current = storagePercentKey;
      setAlertButton(true);
      setShowAllAlerts(false);
      updateAlerts((prev) => [
        ...prev.filter((alert) => alert.type !== 'storage'),
        {
          id: generateId(),
          type: 'storage',
          message: message,
          important: true,
          storagePercentKey,
        },
      ]);
    };

    socket.on('successSeriesImport', handleSuccess);
    socket.on('errorSeriesImport', handleError);
    socket.on('storageFullAlert', handleStorageAlert);
    socket.on('storageAlert', handleStorageAlert);

    return () => {
      socket.off('successSeriesImport', handleSuccess);
      socket.off('errorSeriesImport', handleError);
      socket.off('storageFullAlert', handleStorageAlert);
      socket.off('storageAlert', handleStorageAlert);
    };
  }, [socket, general.storageAlertNotification]);


  useEffect(() => {
    if (general.storageAlertNotification !== false) return;

    lastStorageAlertPercentKeyRef.current = null;
    const nextAlerts = alertsRef.current.filter(
      (alert) => alert.type !== 'storage',
    );
    alertsRef.current = nextAlerts;
    setAlerts(nextAlerts);
    if (nextAlerts.length === 0) {
      setAlertButton(false);
      setShowAllAlerts(false);
    }
  }, [general.storageAlertNotification]);

  const openSettings = () => {
    setIsSettingsOpen(true);
    console.debug('test clicked');
  };
  const closeSettings = () => {
    setIsSettingsOpen(false);
    console.debug('test clicked');
  };
  const visibleAlerts = showAllAlerts
    ? alerts
    : [
        ...alerts.filter((a) => a.important),
        ...alerts.filter((a) => !a.important).slice(-5),
      ];

  renderers['MainShell'] = {
    render: async (args?: any) => {
      const { type, success, message } = args;
      if (type === 'captureMessage') {
        setAlertButton(true);
        updateAlerts((prev) => [
          ...prev,
          {
            id: generateId(),
            type: success ? 'success' : 'error',
            message: message,
            important: true,
          },
        ]);
      }
    },
  };

  return (
    <div className={styles.mainShell}>
      {alertButton ? (
        <div className={styles.logBox}>
          <div className={styles.logHeader}>
            <div
              className={styles.logAlertsBox}
              onClick={() => setShowAllAlerts((prev) => !prev)}
            >
              <div className={styles.logAlertsBoxContainer}>
                <div className={styles.logAlertsBoxContainer2}>
                  <div className={styles.logAlertsBoxContainer3}>
                    {showAllAlerts ? `All Alerts` : `Recent 5 Alerts`}
                  </div>
                </div>
              </div>
            </div>
            <div
              className={styles.logAlertsBoxXMark}
              onClick={closeAlerts}
            >
              <Primary className={styles.primary} />
              <Secondary className={styles.secondary} />
              {/* <AlertClose /> */}
            </div>
          </div>
          <div className={styles.logContainer}>
            <div className={styles.logContainer2}>
              {visibleAlerts.map((alert) => (
                <div
                  key={alert.id}
                  className={`${styles.logModal} ${styles[alert.type]}`}
                  onClick={() => {
                    updateAlerts((prev) =>
                      prev.filter((item) => item.id !== alert.id),
                    );
                  }}
                >
                  <div className={styles.logModalFrame}>
                    <div className={styles.logModalFrame2}>
                      {/* <span className="alert-icon"> */}
                      {alert.type === 'success' && <SuccessIcon />}
                      {alert.type === 'error' && <FailedIcon />}
                      {alert.type === 'storage' && <WarningIcon />}
                      {/* </span> */}
                      <span className={styles.logModalText}>
                        {alert.message}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}
      <TopMenu onSettingsClick={openSettings} />
      {/* <div className={`${styles.mainContent} ${isAnalysisPage ? styles.analysisMode : ''}`}> */}
      <Outlet />
      {/* </div> */}
    </div>
  );
}

export default MainShell;
